(function () {
  'use strict';

  // Schema source of truth: UserFiles/HANDOFF.md
  // Each product entry in the JSON has these short keys:
  //   h  handle, n  name, s  set code, sn  set name, c  card number,
  //   r  rarity, t  supertype (Pokémon|Trainer), pt  print type,
  //   p  price in cents AUD (LOWEST in-stock variant), i  in stock,
  //   nv in-stock variant count (0 → sold out, 1 → single price, 2+ → FROM)

  // Bump this key whenever the JSON schema OR the set of distinct values in
  // a categorical field (e.g. `e` / series) changes upstream, so cached
  // copies in users' sessionStorage are invalidated on next page load
  // instead of serving stale data forever.
  // v2: 2026-05-17 — series merged from 17→13 values (WOTC, Promo buckets).
  // v3: 2026-05-17 — Base folded into WOTC (12 values); payload now ships a
  //                  top-level series_order field; theme reads it instead of
  //                  hard-coding the order.
  var SESSION_KEY = 'manganoSinglesIndex_v3';

  // Bucket definitions — confirm with Jed before launch (see HANDOFF).
  var RARITY_BUCKETS = [
    { label: 'Common',           values: ['Common', '1 Diamond'] },
    { label: 'Uncommon',         values: ['Uncommon', '2 Diamond'] },
    { label: 'Rare',             values: ['Rare', 'Rare Holo', '3 Diamond', '1 Star'] },
    { label: 'Ultra Rare',       values: ['Rare Ultra', 'Rare Holo EX', 'Rare Holo V', 'Rare Holo GX', 'Ultra Rare', 'Double Rare', '4 Diamond', '2 Star', 'ACE SPEC Rare'] },
    { label: 'Secret / Special', values: ['Rare Secret', 'Rare Rainbow', 'Hyper Rare', 'Special Illustration Rare', 'Illustration Rare', 'Shiny Rare', 'Rare Shiny', '3 Star'] },
    { label: 'Promo',            values: ['Promo', 'Pocket Promo', 'Black Star Promo'] }
  ];

  function rarityBucket(r) {
    if (!r) return 'Other';
    for (var i = 0; i < RARITY_BUCKETS.length; i++) {
      if (RARITY_BUCKETS[i].values.indexOf(r) !== -1) return RARITY_BUCKETS[i].label;
    }
    return 'Other';
  }

  function printTypeBucket(pt) {
    if (!pt) return 'Other';
    if (/^1st Edition/i.test(pt)) return '1st Edition';
    if (/Shadowless/i.test(pt))   return 'Shadowless';
    if (/Reverse Holo/i.test(pt)) return 'Reverse Holo';
    if (/Holo/i.test(pt))         return 'Holo';
    if (/Non-Holo/i.test(pt) || /^Unlimited/i.test(pt)) return 'Non-Holo';
    return 'Special';
  }

  // Compact display of the print type for the card metadata row — keeps
  // long phrasings ("1st Edition Shadowless Holo") from wrapping awkwardly.
  function printTypeAbbrev(pt) {
    if (!pt) return '';
    return pt
      .replace(/1st Edition/gi, '1st Ed')
      .replace(/Shadowless/gi, 'SL')
      .replace(/Reverse Holo/gi, 'Rev Holo')
      .replace(/Unlimited /gi, 'Unl ')
      .replace(/Cosmos Holo/gi, 'Cosmos')
      .replace(/Cracked Ice Holo/gi, 'Cracked Ice');
  }

  var PRICE_BUCKETS = [
    { label: '$0 – $5',     min: 0,      max: 500 },
    { label: '$5 – $20',    min: 500,    max: 2000 },
    { label: '$20 – $100',  min: 2000,   max: 10000 },
    { label: '$100 – $500', min: 10000,  max: 50000 },
    { label: '$500+',       min: 50000,  max: Infinity }
  ];

  // The JSON payload now ships a top-level `series_order` array (see
  // HANDOFF.md schema, "Top-level fields") — pre-sorted newest era → oldest
  // with "Promo" pinned to the end, auto-derived from each merged series'
  // newest set release_date. Storefront uses payload.series_order directly,
  // so when a new TCG generation ships the order updates on its own with
  // no JS change. This constant is a **fallback only** for the unlikely
  // case where the payload arrives without `series_order` (e.g. a partial
  // build, or a stale cached payload from before the field shipped). Keep
  // it aligned with the payload convention: newest → oldest, Promo last.
  var SERIES_ORDER_FALLBACK = [
    'Mega Evolution',
    'Scarlet & Violet',
    'Sword & Shield',
    'Sun & Moon',
    'XY',
    'Black & White',
    'HeartGold & SoulSilver',
    'Platinum',
    'Diamond & Pearl',
    'EX',
    'WOTC',
    'Promo'
  ];

  function formatPrice(cents) {
    if (cents == null) return '';
    return '$' + (cents / 100).toFixed(2);
  }

  // Shopify CDN supports `?width=N` for on-the-fly resizing. Cards render
  // at ~200px wide on desktop, so a 400px source covers most density-1
  // displays and a 800px srcset entry handles retina. Original files are
  // ~80KB at 1000×1200; a 400px variant is ~10–15KB — ~5× faster to fetch,
  // which is the difference between "first paint after pagination feels
  // instant" vs "blank cards for a beat". R2 fallback URLs (a tiny number
  // of in-flight rows per HANDOFF.md) don't support the param, so we only
  // apply the transform to cdn.shopify.com origins.
  function shopifyImageWidth(url, width) {
    if (!url || url.indexOf('cdn.shopify.com') === -1) return url;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // Live pixel offset used to park the search/sort bar near the top of the
  // viewport after a page change. The product masthead is hidden on these
  // pages, so .collection-search-wrapper is the first content; we land it just
  // below where the header sits. We only RESERVE the header's height when the
  // header is actually sticky/fixed (then it overlaps content) — with the
  // current non-sticky header (sticky_header off) it scrolls away, so we just
  // leave a small breathing gap. Measured live rather than via --header-height
  // (that var is only set by the homepage slideshow JS, so it's unset here).
  function stickyHeaderOffset() {
    var GAP = 16;
    var h = document.querySelector('.site-header');
    if (h) {
      var pos = getComputedStyle(h).position;
      if (pos === 'sticky' || pos === 'fixed') {
        return Math.round(h.getBoundingClientRect().height) + GAP;
      }
    }
    return GAP;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (k === 'class')      node.className = attrs[k];
        else if (k === 'text')  node.textContent = attrs[k];
        else if (k === 'html')  node.innerHTML = attrs[k];
        else if (k.indexOf('on') === 0 && typeof attrs[k] === 'function') node.addEventListener(k.slice(2), attrs[k]);
        else if (attrs[k] === true)  node.setAttribute(k, '');
        else if (attrs[k] === false || attrs[k] == null) {}
        else node.setAttribute(k, attrs[k]);
      }
    }
    if (children) {
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if (c == null) continue;
        node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      }
    }
    return node;
  }

  // Inline SVG strings the theme uses, to avoid an extra Liquid render call.
  var ICON_CHEVRON = '<svg aria-hidden="true" focusable="false" role="presentation" width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-chevron-down"><path class="icon-chevron-down-left" d="M4 4.5L7 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path><path class="icon-chevron-down-right" d="M4 4.5L1 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path></svg>';
  var ICON_CHECKMARK = '<svg class="checkmark" aria-hidden="true" focusable="false" role="presentation" width="14" height="14" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><path class="checkmark__check" fill="none" d="M2.5 7L5.5 10L11.5 4"></path><path class="checkmark__indeterminate" fill="none" d="M2.5 7L6 7L12 7"></path></svg>';
  // Pulled from snippets/icon-library.liquid id 'icon-remove' so the active
  // filter pill renders the same SVG the native theme would.
  var ICON_REMOVE = '<svg aria-hidden="true" focusable="false" role="presentation" width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M6.08785659,5 L9.77469752,1.31315906 L8.68684094,0.225302476 L5,3.91214341 L1.31315906,0.225302476 L0.225302476,1.31315906 L3.91214341,5 L0.225302476,8.68684094 L1.31315906,9.77469752 L5,6.08785659 L8.68684094,9.77469752 L9.77469752,8.68684094 L6.08785659,5 Z"></path></svg>';

  // ─── Index loader ────────────────────────────────────────────────
  // Server now rebuilds the filter index every 5 minutes (was nightly) and
  // ships it with Cache-Control: max-age=60. To honour that "≤60s in-session
  // staleness" promise, the sessionStorage cache below also enforces a 60s
  // TTL against the payload's own `generated_at` timestamp — otherwise a
  // long-lived tab would pin the first-fetched copy forever regardless of
  // what max-age says.
  var INDEX_TTL_MS = 60 * 1000;

  function loadIndex(url) {
    try {
      var cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.products) {
          var generatedAt = parsed.generated_at ? Date.parse(parsed.generated_at) : NaN;
          if (isFinite(generatedAt) && (Date.now() - generatedAt) < INDEX_TTL_MS) {
            return Promise.resolve(parsed);
          }
          // Fall through to refetch — payload is older than the TTL, or
          // lacks a parseable `generated_at` (older cached build).
        }
      }
    } catch (_) {}

    return fetch(url, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('Index fetch failed: HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch (_) {}
        return data;
      });
  }

  // ─── State (querystring-synced) ──────────────────────────────────
  // Default sort is price high → low: the page should land on the
  // most valuable singles first; filters refine from there.
  var DEFAULT_SORT = 'price-desc';

  function defaultState() {
    return {
      series: [], sets: [], rarities: [], types: [], prints: [], prices: [],
      inStock: false,
      sort: DEFAULT_SORT,
      page: 1
    };
  }

  function readStateFromUrl() {
    var s = defaultState();
    var p = new URLSearchParams(window.location.search);
    if (p.get('series')) s.series   = p.get('series').split(',').filter(Boolean);
    if (p.get('set'))    s.sets     = p.get('set').split(',').filter(Boolean);
    if (p.get('rarity')) s.rarities = p.get('rarity').split(',').filter(Boolean);
    if (p.get('type'))   s.types    = p.get('type').split(',').filter(Boolean);
    if (p.get('print'))  s.prints   = p.get('print').split(',').filter(Boolean);
    if (p.get('price'))  s.prices   = p.get('price').split(',').filter(Boolean);
    if (p.get('stock') === '1') s.inStock = true;
    if (p.get('sort'))   s.sort     = p.get('sort');
    if (p.get('page'))   s.page     = Math.max(1, parseInt(p.get('page'), 10) || 1);
    return s;
  }

  function writeStateToUrl(state) {
    var p = new URLSearchParams();
    if (state.series.length)   p.set('series', state.series.join(','));
    if (state.sets.length)     p.set('set', state.sets.join(','));
    if (state.rarities.length) p.set('rarity', state.rarities.join(','));
    if (state.types.length)    p.set('type', state.types.join(','));
    if (state.prints.length)   p.set('print', state.prints.join(','));
    if (state.prices.length)   p.set('price', state.prices.join(','));
    if (state.inStock)         p.set('stock', '1');
    if (state.sort && state.sort !== DEFAULT_SORT) p.set('sort', state.sort);
    if (state.page > 1)        p.set('page', String(state.page));
    var qs = p.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  // ─── Filter / sort ───────────────────────────────────────────────
  function applyFilters(products, state, searchTerm) {
    var q = searchTerm ? searchTerm.toLowerCase() : '';
    var seriesSet = state.series.length   ? new Set(state.series)   : null;
    var setSet     = state.sets.length     ? new Set(state.sets)     : null;
    var raritySet  = state.rarities.length ? new Set(state.rarities) : null;
    var typeSet    = state.types.length    ? new Set(state.types)    : null;
    var printSet   = state.prints.length   ? new Set(state.prints)   : null;
    var priceBands = state.prices.length
      ? PRICE_BUCKETS.filter(function (b) { return state.prices.indexOf(b.label) !== -1; })
      : null;

    var out = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (q && p.n.toLowerCase().indexOf(q) === -1) continue;
      if (seriesSet && !seriesSet.has(p.e || 'Other')) continue;
      if (setSet && !setSet.has(p.s)) continue;
      if (raritySet && !raritySet.has(rarityBucket(p.r))) continue;
      if (typeSet && !typeSet.has(p.t)) continue;
      if (printSet && !printSet.has(printTypeBucket(p.pt))) continue;
      if (priceBands) {
        var hit = false;
        for (var b = 0; b < priceBands.length; b++) {
          if (p.p >= priceBands[b].min && p.p < priceBands[b].max) { hit = true; break; }
        }
        if (!hit) continue;
      }
      if (state.inStock && !p.i) continue;
      out.push(p);
    }
    return out;
  }

  // Stock tier for sorting: in-stock cards always rank ahead of sold-out
  // ones, whatever sort is chosen. Mirrors the `nv`/`i` fallback the card
  // renderer uses (see renderCard) so the tiering and the Sold Out button
  // always agree on what "in stock" means.
  function inStockRank(p) {
    var nv = typeof p.nv === 'number' ? p.nv : (p.i ? 2 : 0);
    return nv > 0 ? 0 : 1;
  }

  // Sort is two-tier: in-stock first, sold-out after, with the chosen sort
  // applied WITHIN each tier. So "price high → low" leads with the dearest
  // in-stock card, then the sold-out block follows in its own high→low
  // order. `featured` keeps the JSON's native order within each tier —
  // Array.prototype.sort is stable (ES2019), so a 0 from `within` preserves
  // input order.
  function applySort(rows, sort) {
    var sorted = rows.slice();
    var within;
    if (sort === 'price-asc')       within = function (a, b) { return a.p - b.p; };
    else if (sort === 'price-desc') within = function (a, b) { return b.p - a.p; };
    else if (sort === 'name-asc')   within = function (a, b) { return a.n.localeCompare(b.n); };
    else if (sort === 'name-desc')  within = function (a, b) { return b.n.localeCompare(a.n); };
    else                            within = function () { return 0; }; // featured
    sorted.sort(function (a, b) {
      var tier = inStockRank(a) - inStockRank(b);
      return tier !== 0 ? tier : within(a, b);
    });
    return sorted;
  }

  function computeFacets(products) {
    var series = Object.create(null),
        sets = Object.create(null), rarities = Object.create(null),
        types = Object.create(null), prints = Object.create(null),
        prices = Object.create(null), stock = 0;
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var eb = p.e || 'Other';    series[eb]   = (series[eb] || 0) + 1;
      if (!sets[p.s]) sets[p.s] = { code: p.s, name: p.sn || p.s, count: 0 };
      sets[p.s].count++;
      var rb = rarityBucket(p.r); rarities[rb] = (rarities[rb] || 0) + 1;
      var tb = p.t || 'Other';    types[tb]    = (types[tb] || 0) + 1;
      var pb = printTypeBucket(p.pt); prints[pb] = (prints[pb] || 0) + 1;
      for (var b = 0; b < PRICE_BUCKETS.length; b++) {
        var band = PRICE_BUCKETS[b];
        if (p.p >= band.min && p.p < band.max) { prices[band.label] = (prices[band.label] || 0) + 1; break; }
      }
      if (p.i) stock++;
    }
    var setList = Object.keys(sets).map(function (k) { return sets[k]; });
    setList.sort(function (a, b) { return a.name.localeCompare(b.name); });
    return { series: series, sets: setList, rarities: rarities, types: types, prints: prints, prices: prices, stock: stock };
  }

  function toggleArrayValue(arr, value, on) {
    var idx = arr.indexOf(value);
    if (on && idx === -1) arr.push(value);
    if (!on && idx !== -1) arr.splice(idx, 1);
  }

  // ─── Sidebar render (native collection-filters__* markup) ────────
  // Each facet group's counts reflect what would happen if the user toggled
  // a value in that group, given every OTHER active filter. So selecting
  // "Common" in Rarity makes the Price counts shrink to "Commons in each
  // band", but the Rarity group itself still shows the unrestricted-by-
  // rarity counts (so you can switch from Common to Uncommon without
  // first having to untick Common). Standard faceted-search behaviour —
  // matches what Boost / Searchanise / Shopify native do.
  function renderFacets(root, allProducts, state, searchTerm, onChange, seriesOrder, openGroups) {
    var container = root.querySelector('[data-singles-filter-facets]');
    container.innerHTML = '';

    function countsExcluding(key) {
      var stateCopy = {
        series: state.series, sets: state.sets, rarities: state.rarities,
        types: state.types, prints: state.prints, prices: state.prices,
        inStock: state.inStock, sort: state.sort, page: state.page
      };
      if (key === 'inStock') stateCopy.inStock = false;
      else if (key) stateCopy[key] = [];
      return computeFacets(applyFilters(allProducts, stateCopy, searchTerm));
    }

    var seriesFacets = countsExcluding('series');
    var typeFacets   = countsExcluding('types');
    var rarityFacets = countsExcluding('rarities');
    var printFacets  = countsExcluding('prints');
    var priceFacets  = countsExcluding('prices');
    var setFacets    = countsExcluding('sets');
    var stockFacets  = countsExcluding('inStock');

    // Series + Set sit at the top of the sidebar as the geographic
    // hierarchy: Series (generation) → Set (specific release). Order comes
    // from the payload's top-level series_order array (newest era first,
    // Promo pinned to the end, auto-derived from set release_dates). Any
    // value present in the data but missing from series_order lands
    // alphabetically at the bottom — defensive coverage for partial /
    // mid-deploy payloads only; under normal operation the payload's
    // series_order covers every value in the index.
    var order = (seriesOrder && seriesOrder.length) ? seriesOrder : SERIES_ORDER_FALLBACK;
    var presentSeries = Object.keys(seriesFacets.series);
    var orderedSeries = order.filter(function (s) { return presentSeries.indexOf(s) !== -1; });
    var extraSeries = presentSeries
      .filter(function (s) { return order.indexOf(s) === -1; })
      .sort(function (a, b) { return a.localeCompare(b); });
    var seriesList = orderedSeries.concat(extraSeries);

    container.appendChild(filterGroup('Series', seriesList.map(function (s) {
      return { label: s, value: s, count: seriesFacets.series[s] || 0, active: state.series.indexOf(s) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.series, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Set', setFacets.sets.map(function (s) {
      return { label: s.name, value: s.code, count: s.count, active: state.sets.indexOf(s.code) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.sets, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ true, openGroups));

    container.appendChild(filterGroup('Card type', [
      { label: 'Pokémon',  value: 'Pokémon',  count: typeFacets.types['Pokémon']  || 0, active: state.types.indexOf('Pokémon')  !== -1 },
      { label: 'Trainer',  value: 'Trainer',  count: typeFacets.types['Trainer']  || 0, active: state.types.indexOf('Trainer')  !== -1 }
    ], function (val, on) {
      toggleArrayValue(state.types, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Rarity', RARITY_BUCKETS.map(function (b) {
      return { label: b.label, value: b.label, count: rarityFacets.rarities[b.label] || 0, active: state.rarities.indexOf(b.label) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.rarities, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Print', ['Holo', 'Reverse Holo', 'Non-Holo', '1st Edition', 'Shadowless', 'Special'].map(function (k) {
      return { label: k, value: k, count: printFacets.prints[k] || 0, active: state.prints.indexOf(k) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.prints, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Price', PRICE_BUCKETS.map(function (b) {
      return { label: b.label, value: b.label, count: priceFacets.prices[b.label] || 0, active: state.prices.indexOf(b.label) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.prices, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Availability', [
      { label: 'In stock', value: 'in', count: stockFacets.stock, active: state.inStock }
    ], function (val, on) {
      state.inStock = on;
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));
  }

  // Render one accordion group using the exact native DOM the theme ships with.
  // `openGroups` (optional) is a shared object keyed by group title, persisting
  // user open/close choice across renderFacets re-renders. Without it the group
  // would re-derive its open state from "any active value" on every toggle —
  // which causes the group to collapse the moment the user unchecks the last
  // active value in it.
  function filterGroup(title, values, onToggle, scrollable, openGroups) {
    var anyActive = values.some(function (v) { return v.active; });
    // First-time render: open if any value is active (e.g. shared URL with
    // ?series=XY). Once a render has happened, the stored value wins so the
    // group never auto-collapses on an unrelated re-render.
    var expanded;
    if (openGroups && typeof openGroups[title] === 'boolean') {
      expanded = openGroups[title];
    } else {
      expanded = anyActive;
      if (openGroups) openGroups[title] = expanded;
    }

    var heading = el('button', {
      type: 'button',
      class: 'collection-filters__filter-group-heading productgrid--sidebar-button' + (expanded ? ' productgrid--sidebar-button-active' : ''),
      'aria-expanded': expanded ? 'true' : 'false'
    }, [
      el('div', { class: 'collection-filters__filter-title' }, [title]),
      el('div', { class: 'collection-filters__filter-icon-wrapper', tabindex: '-1' }, [
        el('span', { class: 'collection-filters__filter-icon', html: ICON_CHEVRON })
      ])
    ]);

    var listClasses = 'collection-filters__filter collection-filters__filter-list' + (expanded ? ' filter-group-active' : '');
    var list = el('ul', {
      class: listClasses,
      'data-filter-group-list': '',
      'data-accordion-content': '',
      'data-accordion-state': expanded ? 'open' : 'closed'
    });
    if (!expanded) list.style.display = 'none';

    // Render every value, but mark count==0 (and not already active) as
    // disabled — clicking it would empty the grid, so we let it sit there
    // greyed-out instead of vanishing. Less jarring than hiding/reflowing
    // the list every time the user toggles an adjacent facet.
    values.forEach(function (v) {
      var disabled = !v.count && !v.active;
      list.appendChild(filterItem(v, onToggle, disabled));
    });

    heading.addEventListener('click', function () {
      var nowOpen = heading.getAttribute('aria-expanded') !== 'true';
      heading.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      heading.classList.toggle('productgrid--sidebar-button-active', nowOpen);
      list.classList.toggle('filter-group-active', nowOpen);
      list.setAttribute('data-accordion-state', nowOpen ? 'open' : 'closed');
      list.style.display = nowOpen ? '' : 'none';
      if (openGroups) openGroups[title] = nowOpen;
    });

    if (scrollable) list.classList.add('singles-filter--facet-scroll');

    return el('div', { class: 'collection-filters__filter-group', 'aria-label': title, 'data-filter-group': '' }, [heading, list]);
  }

  function filterItem(v, onToggle, disabled) {
    var liClass = 'collection-filters__filter-list-item';
    if (v.active) liClass += ' collection-filters__filter-list-item--active';
    if (disabled) liClass += ' collection-filters__filter-list-item--disabled';
    var li = el('li', {
      class: liClass,
      'data-filter-open': v.active ? 'true' : 'false'
    });

    var linkAttrs = {
      class: 'collection-filters__filter-link',
      href: '#',
      'data-filter-input': ''
    };
    if (disabled) {
      linkAttrs['data-disabled'] = '';
      linkAttrs['aria-disabled'] = 'true';
      linkAttrs.onclick = function (e) { e.preventDefault(); };
    } else {
      linkAttrs.onclick = function (e) {
        e.preventDefault();
        var willActivate = !v.active;
        onToggle(v.value, willActivate);
      };
    }
    var link = el('a', linkAttrs, [
      el('span', { class: 'filter-icon--checkbox', html: ICON_CHECKMARK }),
      el('span', { class: 'collection-filters__filter-list-item-text' }, [
        v.label + (v.count != null ? ' (' + v.count + ')' : '')
      ])
    ]);

    if (v.active) {
      // Match the theme's checked-state markup so the brand checkmark animates in.
      var svg = link.querySelector('.checkmark');
      if (svg) {
        svg.setAttribute('data-animation-state', 'checked');
        Array.prototype.forEach.call(svg.querySelectorAll('path'), function (p) {
          p.setAttribute('data-animation-state', 'checked');
        });
      }
      link.setAttribute('data-filter-active', '');
    }

    li.appendChild(link);
    return li;
  }

  // ─── Active filter pills ─────────────────────────────────────────
  // Mounts a `.productgrid--filters > ul.active-filter-group` block
  // ONLY when there are active filters, so when the user has nothing
  // selected the slot stays empty and the theme's 28px filter-row
  // margin doesn't push the grid down (parity with the sealed page).
  function renderActivePills(root, state, allFacets, onChange) {
    var host = root.querySelector('[data-singles-filter-active-host]');
    host.innerHTML = '';

    function setNameForCode(code) {
      var entry = allFacets.sets.filter(function (s) { return s.code === code; })[0];
      return entry ? entry.name : code;
    }

    var pills = [];
    function add(label, onRemove) {
      pills.push({ label: label, onRemove: onRemove });
    }

    state.series.forEach(function (s) {
      add(s, function () { toggleArrayValue(state.series, s, false); state.page = 1; onChange(); });
    });
    state.sets.forEach(function (c) {
      add(setNameForCode(c), function () { toggleArrayValue(state.sets, c, false); state.page = 1; onChange(); });
    });
    state.rarities.forEach(function (r) {
      add(r, function () { toggleArrayValue(state.rarities, r, false); state.page = 1; onChange(); });
    });
    state.types.forEach(function (t) {
      add(t, function () { toggleArrayValue(state.types, t, false); state.page = 1; onChange(); });
    });
    state.prints.forEach(function (p) {
      add(p, function () { toggleArrayValue(state.prints, p, false); state.page = 1; onChange(); });
    });
    state.prices.forEach(function (p) {
      add(p, function () { toggleArrayValue(state.prices, p, false); state.page = 1; onChange(); });
    });
    if (state.inStock) {
      add('In stock', function () { state.inStock = false; state.page = 1; onChange(); });
    }

    if (pills.length === 0) return;

    // Native pill structure (mirrors snippets/product-grid-filters-active.liquid)
    // so .active-filter-group__item-wrapper > .active-filter-group__item picks
    // up the brand-blue overrides at theme.css.liquid line 24766.
    //
    // Clear all is rendered FIRST (not last as in the native theme). With many
    // active filters the row fills, and a trailing Clear all wraps onto its
    // own line and looks stranded. Putting it first guarantees it's always
    // visible at top-left of the first row, never below the pills.
    var ul = el('ul', { class: 'active-filter-group' });
    ul.appendChild(el('li', { class: 'active-filter-group--remove-all' }, [
      el('a', {
        class: 'filter-item-clear-all',
        href: '#',
        onclick: function (e) {
          e.preventDefault();
          var keepSort = state.sort;
          Object.assign(state, defaultState());
          state.sort = keepSort;
          onChange();
        }
      }, ['Clear all'])
    ]));
    pills.forEach(function (p) {
      var link = el('a', {
        class: 'active-filter-group__item',
        href: '#',
        'data-filter-input': '',
        onclick: function (e) { e.preventDefault(); p.onRemove(); }
      }, [
        el('span', { class: 'active-filter-text' }, [p.label]),
        el('span', { class: 'filter-icon--remove', 'aria-hidden': 'true', html: ICON_REMOVE })
      ]);
      ul.appendChild(el('li', { class: 'active-filter-group__item-wrapper' }, [link]));
    });

    // Use our own class — `productgrid--filters` is hidden by the theme below
    // 860px (theme.css.liquid line 17436), which made the pill row vanish on
    // tablet/mobile. `.singles-filter--active-pills` styles in our CSS block.
    host.appendChild(el('div', { class: 'singles-filter--active-pills' }, [ul]));
  }

  // ─── Card render (native productgrid--item productitem markup) ───
  function renderCards(root, rows, state, perPage) {
    var grid = root.querySelector('[data-singles-filter-grid]');
    grid.removeAttribute('aria-busy');
    grid.innerHTML = '';

    var total = rows.length;
    if (total === 0) {
      // Render the empty state inline as a single grid child instead of a
      // separate sibling — avoids the always-rendered no-results sibling
      // visible at the page bottom.
      grid.appendChild(el('div', { class: 'singles-filter--state singles-filter--state-empty' }, [
        el('h2', { class: 'singles-filter--state-title' }, ['No cards match those filters.']),
        el('button', {
          type: 'button',
          class: 'singles-filter--state-clear',
          'data-singles-filter-clear': '',
          onclick: function () {} // bubbles to root delegate
        }, ['Clear filters'])
      ]));
      return;
    }

    var start = (state.page - 1) * perPage;
    var pageRows = rows.slice(start, start + perPage);

    pageRows.forEach(function (p) { grid.appendChild(renderCard(p)); });

    // Per-row title-height balancing lives in sections/collection-search.liquid.
    // The shared script exposes `window.__balanceTitleHeights` (a debounced
    // scheduler) so JS-rendered grids can request a rebalance after every
    // render — pagination clicks, filter changes, sort changes, initial
    // load. Relying on the script's MutationObserver was unreliable here
    // because the observer fires mid-render, before images decode and
    // titles wrap to their final line counts.
    if (typeof window.__balanceTitleHeights === 'function') {
      window.__balanceTitleHeights();
    }

    // Defensive: images set loading="eager" but decode is still async, and
    // title wrap can shift slightly as the layout settles. Schedule one
    // more rebalance on each not-yet-complete image's load.
    grid.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', function () {
        if (typeof window.__balanceTitleHeights === 'function') {
          window.__balanceTitleHeights();
        }
      }, { once: true });
    });
  }

  function renderCard(p) {
    var link = '/products/' + encodeURIComponent(p.h);

    // Set badge above the image — same DOM the Liquid card uses
    // (snippets/product-grid-item.liquid), so the existing CSS for
    // `.productitem--set-badge` applies.
    var setBadge = p.b ? el('div', { class: 'productitem--set-badge', 'aria-hidden': 'true' }, [
      el('img', { src: p.b, alt: p.sn || '', loading: 'lazy', decoding: 'async' })
    ]) : null;

    // Real Shopify CDN image. Native files are 1000×1200 (5:6) per HANDOFF.md;
    // we serve a 400px variant by default (with an 800px srcset entry for
    // retina) so the file is ~5× smaller and renders near-instantly on
    // pagination. `loading=eager` skips the IntersectionObserver delay so
    // the browser starts fetching the moment the DOM is inserted — pairs
    // with the small file size to make new-page paint feel snappy. The
    // explicit width/height attrs reserve the layout box so the page
    // doesn't reflow as images decode.
    var imgSrc = shopifyImageWidth(p.img, 400);
    var imgSrcset = shopifyImageWidth(p.img, 400) + ' 1x, ' +
                    shopifyImageWidth(p.img, 800) + ' 2x';
    // alt="" is intentional — the product name is rendered as an <h2> right
    // below this image, so duplicating it as alt creates redundant SR output
    // AND, more visibly, renders as a styled link text under the parent <a>
    // during the brief moment between DOM insertion and image decode. The
    // <a>'s aria-label keeps the link semantically labelled for assistive
    // tech without making alt text the loading-state fallback.
    var imageBlock = el('a', {
      class: 'singles-filter--card-image',
      href: link,
      'aria-label': p.n
    }, [
      el('img', {
        src: imgSrc,
        srcset: imgSrcset,
        alt: '',
        loading: 'eager',
        decoding: 'async',
        fetchpriority: 'high',
        width: '1000',
        height: '1200',
        class: 'singles-filter--card-image-img'
      })
    ]);

    var title = el('h2', { class: 'productitem--title' }, [
      el('a', { href: link }, [p.n])
    ]);

    // Restore the metadata row that the original singles cards showed
    // beneath the title — `{card-number} · {rarity} · {print-type}`. Uses
    // the existing `.productitem--card-number` class so it inherits the
    // grey centred styling already in theme.css.liquid (line 23304).
    var metaParts = [];
    if (p.c) metaParts.push(p.c);
    if (p.r) metaParts.push(p.r);
    if (p.pt) metaParts.push(printTypeAbbrev(p.pt));
    var metaRow = metaParts.length
      ? el('div', { class: 'productitem--card-number' }, [metaParts.join(' · ')])
      : null;

    var price = el('div', { class: 'price productitem__price' }, [
      el('div', { class: 'price__current', 'data-price-container': '' }, [
        el('span', { class: 'money', 'data-price': '' }, [formatPrice(p.p)])
      ])
    ]);

    var info = el('div', { class: 'productitem--info' }, [title, metaRow, price]);

    // Bottom button — driven by `nv` (in-stock variant count) per HANDOFF:
    //   nv === 0 → Sold Out (grayed <span>, not clickable, no price)
    //   nv === 1 → ADD TO CART | $price
    //   nv >= 2  → ADD TO CART | FROM $price
    // Falls back to `p.i` for older JSON builds that predate the `nv`
    // field — treats any in-stock card as 2+ (FROM) in that case.
    var instockCount = typeof p.nv === 'number' ? p.nv : (p.i ? 2 : 0);

    var atcNode;
    if (instockCount === 0) {
      atcNode = el('span', {
        class: 'productitem--action-trigger productitem--action-atc button-primary singles-filter--price-button disabled',
        'aria-disabled': 'true'
      }, ['Sold Out']);
    } else {
      var atcLabel = instockCount === 1
        ? 'ADD TO CART | ' + formatPrice(p.p)
        : 'ADD TO CART | FROM ' + formatPrice(p.p);
      atcNode = el('a', {
        class: 'productitem--action-trigger productitem--action-atc button-primary singles-filter--price-button',
        href: link
      }, [atcLabel]);
    }

    var actions = el('div', { class: 'productitem--actions' }, [
      el('div', { class: 'productitem--action quickshop-button' }, [
        el('a', {
          class: 'productitem--action-trigger button-secondary singles-filter--view-button',
          href: link
        }, ['View Card'])
      ]),
      el('div', { class: 'productitem--action atc--button' }, [atcNode])
    ]);

    return el('div', {
      class: 'productgrid--item imagestyle--natural show-actions--mobile singles-filter--card',
      'data-product-item': '',
      'data-product-handle': p.h
    }, [
      setBadge,
      el('div', { class: 'productitem', 'data-product-item-content': '' }, [
        el('div', { class: 'productitem__container' }, [imageBlock, info, actions])
      ])
    ]);
  }

  // ─── Pagination (theme's pagination--inner classes) ──────────────
  // The theme renders prev/next as a chevron SVG inside a `pagination--chevron-{left|right}`
  // span, with the visible "Prev"/"Next" text hidden via `font-size: 0`. Mirror that
  // markup so the existing CSS at theme.css.liquid line 24929+ paints the arrows.
  function renderPagination(root, total, state, perPage, onPageChange) {
    var nav = root.querySelector('[data-singles-filter-pagination]');
    var list = root.querySelector('[data-singles-filter-pagination-list]');
    list.innerHTML = '';

    var pageCount = Math.max(1, Math.ceil(total / perPage));
    if (pageCount <= 1) { nav.hidden = true; return; }
    nav.hidden = false;

    var current = Math.min(state.page, pageCount);
    if (current !== state.page) state.page = current;

    function pageItem(label, page, opts) {
      opts = opts || {};
      var li = el('li', {
        class: 'pagination--' + (opts.kind || 'page') + (opts.active ? ' pagination--active' : '')
      });
      var children;
      if (opts.kind === 'previous') {
        children = [
          el('span', { class: 'pagination--chevron-left', 'aria-hidden': 'true', html: ICON_CHEVRON }),
          'Prev'
        ];
      } else if (opts.kind === 'next') {
        children = [
          'Next',
          el('span', { class: 'pagination--chevron-right', 'aria-hidden': 'true', html: ICON_CHEVRON })
        ];
      } else {
        children = [label];
      }
      if (opts.disabled || opts.active) {
        li.appendChild(el('span', { class: 'pagination--item' }, children));
      } else {
        li.appendChild(el('a', {
          class: 'pagination--item',
          href: '#',
          'aria-label': opts.kind === 'previous' ? 'Go to previous page'
            : opts.kind === 'next' ? 'Go to next page'
            : ('Go to page ' + label),
          onclick: function (e) { e.preventDefault(); state.page = page; onPageChange(); }
        }, children));
      }
      return li;
    }

    if (current > 1) list.appendChild(pageItem(null, current - 1, { kind: 'previous' }));
    paginationNumbers(current, pageCount).forEach(function (n) {
      if (n === '…') list.appendChild(pageItem('…', null, { kind: 'ellipsis', disabled: true }));
      else list.appendChild(pageItem(String(n), n, { active: n === current }));
    });
    if (current < pageCount) list.appendChild(pageItem(null, current + 1, { kind: 'next' }));
  }

  function paginationNumbers(current, total) {
    var out = [], pad = 2;
    function push(n) { if (out.indexOf(n) === -1) out.push(n); }
    push(1);
    for (var i = current - pad; i <= current + pad; i++) {
      if (i > 1 && i < total) push(i);
    }
    if (total > 1) push(total);
    out.sort(function (a, b) { return a - b; });
    var withGaps = [];
    for (var j = 0; j < out.length; j++) {
      if (j > 0 && out[j] - out[j - 1] > 1) withGaps.push('…');
      withGaps.push(out[j]);
    }
    return withGaps;
  }

  // ─── Top-bar count + filter-button label ─────────────────────────
  function renderMeta(root, state, total) {
    var label = root.querySelector('[data-singles-filter-button-label]');
    if (label) {
      var n = state.sets.length + state.rarities.length + state.types.length +
              state.prints.length + state.prices.length + (state.inStock ? 1 : 0);
      label.textContent = n ? 'Filters (' + n + ')' : 'Filters';
    }
  }

  // ─── Bridge to existing collection-search bar ─────────────────────
  // The page already includes the `collection-search` section above main,
  // which gives us a pre-styled search input. Hijack its native input
  // events to drive our filter; otherwise the user gets a duplicate UI.
  function bindCollectionSearchBar(onSearch) {
    var input = document.querySelector('.collection-search-bar input[type="search"], .collection-search-bar input[type="text"]');
    if (!input) return function () { return ''; };
    input.setAttribute('placeholder', 'Search singles by name…');
    input.addEventListener('input', debounce(function () {
      onSearch(input.value.trim());
    }, 180));
    // Block the form submit so it doesn't navigate to /search
    var form = input.closest('form');
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); });
    // Read the input's live value rather than a cached one — that way when
    // Clear Filters programmatically sets input.value = '' (which doesn't
    // fire an `input` event), the next rerender sees an empty query and
    // the full product list comes back instead of staying filtered by the
    // last typed term.
    return function () { return input.value.trim(); };
  }

  // Map Shopify's standard sort values (used by the collection-search
  // dropdown buttons) to the JS sort modes the JSON-driven filter supports.
  // The JSON has no creation-date field, so date sorts collapse to featured.
  var SHOPIFY_SORT_MAP = {
    'manual': 'featured',
    'most-relevant': 'featured',
    'best-selling': 'featured',
    'created-ascending': 'featured',
    'created-descending': 'featured',
    'created': 'featured',
    'price-ascending': 'price-asc',
    'price-descending': 'price-desc',
    'title-ascending': 'name-asc',
    'title-descending': 'name-desc'
  };

  function bindCollectionSearchSort(onSort, state) {
    var dropdown = document.querySelector('[data-sort-dropdown]');
    if (!dropdown) return;

    var buttons = dropdown.querySelectorAll('[data-sort-value]');

    function activate(jsSort) {
      Array.prototype.forEach.call(buttons, function (b) {
        var v = SHOPIFY_SORT_MAP[b.getAttribute('data-sort-value')] || 'featured';
        var isActive = v === jsSort;
        b.classList.toggle('sort-option--active', isActive);
        if (isActive) b.setAttribute('disabled', ''); else b.removeAttribute('disabled');
      });
    }

    Array.prototype.forEach.call(buttons, function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        var v = SHOPIFY_SORT_MAP[b.getAttribute('data-sort-value')] || 'featured';
        activate(v);
        onSort(v);
        // Close the dropdown the same way the existing JS does.
        dropdown.classList.remove('sort-dropdown--open');
      });
    });

    // Reflect the current sort (URL value, or DEFAULT_SORT) on first load.
    activate(state.sort || DEFAULT_SORT);
  }

  // ─── Boot ────────────────────────────────────────────────────────
  function init(root) {
    var indexUrl = root.getAttribute('data-index-url');
    var perPage = parseInt(root.getAttribute('data-products-per-page'), 10) || 24;

    var state = readStateFromUrl();
    var allProducts = [];
    // Server-supplied chronological order for the Series facet (see HANDOFF
    // schema, top-level `series_order`). Falls back to SERIES_ORDER_FALLBACK
    // if the payload doesn't ship it (legacy / partial build).
    var seriesOrder = SERIES_ORDER_FALLBACK;
    // Persistent open/closed state per facet group, keyed by group title.
    // Survives the renderFacets innerHTML wipe on every toggle, so groups
    // don't collapse when a user unchecks the last value in them.
    //   undefined → use default (open iff group has any active value)
    //   true      → user opened (or initial-render-from-URL had active)
    //   false     → user closed
    var openGroups = Object.create(null);

    var getSearch = bindCollectionSearchBar(function (term) {
      state.page = 1;
      rerender();
    });

    function rerender() {
      var q = getSearch();
      var filtered = applyFilters(allProducts, state, q);
      var sorted = applySort(filtered, state.sort);
      var allFacets = computeFacets(allProducts);

      renderFacets(root, allProducts, state, q, rerender, seriesOrder, openGroups);
      renderActivePills(root, state, allFacets, rerender);
      renderCards(root, sorted, state, perPage);
      renderPagination(root, sorted.length, state, perPage, function paginate() {
        writeStateToUrl(state);
        renderCards(root, sorted, state, perPage);
        renderPagination(root, sorted.length, state, perPage, paginate);
        // Park the page so the search/sort bar sits near the top, just under
        // the header. The product masthead is hidden on these pages (see
        // brand-overrides.css), so .collection-search-wrapper is the first
        // visible content — anchor on it, falling back to the grid root if
        // it's somehow absent. Offset by the live header height + gap.
        var anchor = document.querySelector('.collection-search-wrapper') || root;
        var top = anchor.getBoundingClientRect().top + window.scrollY - stickyHeaderOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
      renderMeta(root, state, sorted.length);
      writeStateToUrl(state);
    }

    // Hijack the existing collection-search sort dropdown. Its buttons emit
    // standard Shopify sort values (manual, title-ascending, etc.) — we
    // map those to the JS sort modes the JSON supports.
    bindCollectionSearchSort(function (value) {
      state.sort = value;
      state.page = 1;
      rerender();
    }, state);

    root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-singles-filter-clear]');
      if (!t) return;
      e.preventDefault();
      var keepSort = state.sort;
      Object.assign(state, defaultState());
      state.sort = keepSort;
      var input = document.querySelector('.collection-search-bar input');
      if (input) input.value = '';
      rerender();
    });

    loadIndex(indexUrl)
      .then(function (data) {
        allProducts = data.products || [];
        if (Array.isArray(data.series_order) && data.series_order.length) {
          seriesOrder = data.series_order;
        }
        rerender();
      })
      .catch(function (err) {
        console.error('[singles-filter] failed to load index', err);
        var grid = root.querySelector('[data-singles-filter-grid]');
        grid.removeAttribute('aria-busy');
        grid.innerHTML = '';
        grid.appendChild(el('div', { class: 'singles-filter--state singles-filter--state-error' }, [
          el('p', null, ['Couldn’t load the singles index.']),
          el('p', { class: 'singles-filter--state-error-detail' }, [String(err.message || err)])
        ]));
      });
  }

  function boot() {
    var root = document.querySelector('[data-singles-filter-root]');
    if (root) init(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
